import {
  GoogleGenerativeAI,
  HarmCategory,
  HarmBlockThreshold,
} from "@google/generative-ai";
import { database, ref, set, push, get } from "../config/firebase";

// Configuração da API Key via variável de ambiente do React
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY || "";
const genAI = new GoogleGenerativeAI(API_KEY);

// Configurações de segurança para evitar conteúdo ofensivo
const safetySettings = [
  {
    category: HarmCategory.HARM_CATEGORY_HARASSMENT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
];

// Instruções de sistema para o Tutor IA
const TUTOR_SYSTEM_INSTRUCTION = `
Você é o Tutor de Libras da Bilingua Brasil, um assistente especializado em ensinar a Língua Brasileira de Sinais (Libras) e cultura surda.
Seu objetivo é ser amigável, didático e encorajador.

Diretrizes:
1. Responda sempre em Português, mas use termos de Libras quando apropriado.
2. Explique conceitos gramaticais da Libras (Glosa, classificadores, tipos de verbos).
3. Quando ensinar um sinal ou uma frase em datilologia, use o formato [DATILOLOGIA:PALAVRA] para que o app possa renderizar.
4. Use **negrito** para destacar palavras importantes.
5. Se o usuário perguntar algo fora do escopo de Libras ou educação, redirecione gentilmente a conversa de volta para o aprendizado de sinais.

Exemplo de resposta com datilologia:
"Para dizer seu nome, você usa o alfabeto manual. Meu nome se escreve assim: [DATILOLOGIA:TUTOR]"
`;

// Instruções para o Tradutor de Glosa
const TRANSLATOR_SYSTEM_INSTRUCTION = `
Você é um tradutor especializado em converter frases do Português para a Glosa da Libras.
A Glosa segue a estrutura gramatical da Libras (geralmente Sujeito-Objeto-Verbo ou Tópico-Comentário).

Objetivo:
Receber uma frase em Português e retornar um JSON com:
- glosa: A frase traduzida para Glosa (em letras maiúsculas).
- rule: A regra gramatical principal aplicada (ex: Omissão de preposição, Tempo no início).
- explanation: Uma explicação breve de por que a tradução ficou dessa forma.

Exemplo:
Receba: "Eu vou ao mercado amanhã"
Retorne: {
  "glosa": "AMANHÃ MERCADO EU IR",
  "rule": "Tempo no início da frase e omissão de preposição",
  "explanation": "Em Libras, o tempo vem primeiro para estabelecer o contexto, e não usamos preposições como 'ao'."
}

RETORNE APENAS O JSON, SEM BLOCOS DE CÓDIGO OU TEXTO ADICIONAL.
`;

const geminiService = {
  /**
   * Envia uma mensagem para o Tutor IA e recebe a resposta
   */
  async sendMessageToTutor(
    userId: string,
    message: string,
    history: any[] = []
  ): Promise<string> {
    try {
      const model = genAI.getGenerativeModel({
        model: "gemini-1.5-flash",
        systemInstruction: TUTOR_SYSTEM_INSTRUCTION,
      });

      const chat = model.startChat({
        history: history.map((msg) => ({
          role: msg.role === "assistant" ? "model" : "user",
          parts: [{ text: msg.content }],
        })),
        safetySettings,
      });

      const result = await chat.sendMessage(message);
      const { response } = result;
      const responseText = response.text();

      // Salvar no Firebase
      if (userId) {
        const historyRef = ref(database, `chatHistory/${userId}`);
        const newMessageRef = push(historyRef);
        await set(newMessageRef, {
          role: "user",
          content: message,
          timestamp: Date.now(),
        });
        const assistantMessageRef = push(historyRef);
        await set(assistantMessageRef, {
          role: "assistant",
          content: responseText,
          timestamp: Date.now(),
        });
      }

      return responseText;
    } catch (error) {
      console.error("Erro ao enviar mensagem para o Gemini:", error);
      throw new Error("Não foi possível processar sua mensagem agora.");
    }
  },

  /**
   * Traduz uma frase do Português para Glosa
   */
  /** Palavra ou expressão curta → glosa (mesmo fluxo do app nativo) */
  async generateGlosaForWord(word: string): Promise<{
    glosa: string;
    rule: string;
    explanation: string;
  }> {
    const trimmed = word?.trim() || "";
    if (!trimmed) {
      return {
        glosa: "",
        rule: "",
        explanation: "",
      };
    }
    return this.translateToGlosa(
      `Traduza para a glosa de Libras (estrutura em maiúsculas) apenas esta palavra ou expressão em português: "${trimmed}".`
    );
  },

  async translateToGlosa(text: string): Promise<any> {
    try {
      const model = genAI.getGenerativeModel({
        model: "gemini-1.5-flash",
        systemInstruction: TRANSLATOR_SYSTEM_INSTRUCTION,
      });

      const result = await model.generateContent(text);
      const { response } = result;
      const jsonString = response.text();

      try {
        // Tenta limpar possíveis marcações de Markdown caso o modelo retorne
        const cleanedJson = jsonString
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();
        return JSON.parse(cleanedJson);
      } catch (e) {
        console.error("Erro ao fazer parse do JSON do Tradutor:", e);
        return {
          glosa: text.toUpperCase(),
          rule: "Erro de processamento",
          explanation: "Ocorreu um erro ao estruturar a tradução.",
        };
      }
    } catch (error) {
      console.error("Erro na tradução para Glosa:", error);
      throw new Error("Falha na tradução.");
    }
  },

  /**
   * Busca o histórico de chat do usuário
   */
  async getChatHistory(userId: string): Promise<any[]> {
    try {
      const historyRef = ref(database, `chatHistory/${userId}`);
      const snapshot = await get(historyRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        return Object.values(data).sort(
          (a: any, b: any) => a.timestamp - b.timestamp
        );
      }
      return [];
    } catch (error) {
      console.error("Erro ao buscar histórico de chat:", error);
      return [];
    }
  },
};

export default geminiService;
